import React, { useEffect, useState } from 'react';
import './FirstPage.css';
import { Chart as ChartJS, CategoryScale, LinearScale, TimeScale, Tooltip } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { CandlestickController, CandlestickElement } from 'chartjs-chart-financial';

ChartJS.register(CategoryScale, LinearScale, TimeScale, Tooltip, CandlestickController, CandlestickElement);

const FirstPage = () => {
  const [prices, setPrices] = useState({ bnb: 0, eth: 0, xrp: 0 });
  const [profits, setProfits] = useState({ bnb: '0.00', eth: '0.00', xrp: '0.00' });
  const [candleData, setCandleData] = useState([]);
  const [ethCandleData, setEthCandleData] = useState([]);
  const [xrpCandleData, setXrpCandleData] = useState([]); // ✅ New

  // 🔁 Price + Profit % Loader
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await fetch('https://api.coingecko.com/api/v3/simple/price?ids=binancecoin,ethereum,ripple&vs_currencies=usd');
        const data = await res.json();

        setPrices({
          bnb: data.binancecoin.usd,
          eth: data.ethereum.usd,
          xrp: data.ripple.usd
        });

        const stored = localStorage.getItem('neuron_profit_data');
        const now = Date.now();

        if (stored) {
          const { bnb, eth, xrp, timestamp } = JSON.parse(stored);
          if (now - timestamp < 18000000) {
            setProfits({ bnb, eth, xrp });
            return;
          }
        }

        const newProfits = {
          bnb: (Math.random() * 10 + 20).toFixed(2),
          eth: (Math.random() * 10 + 20).toFixed(2),
          xrp: (Math.random() * 10 + 20).toFixed(2)
        };

        setProfits(newProfits);
        localStorage.setItem('neuron_profit_data', JSON.stringify({ ...newProfits, timestamp: now }));
      } catch (err) {
        console.error('Price fetch error:', err);
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  // 📊 BNB Candle Chart Loader
  useEffect(() => {
    const fetchCandles = async () => {
      const res = await fetch('https://api.binance.com/api/v3/klines?symbol=BNBUSDT&interval=1m&limit=50');
      const data = await res.json();
      const formatted = data.map(d => ({
        x: new Date(d[0]),
        o: parseFloat(d[1]),
        h: parseFloat(d[2]),
        l: parseFloat(d[3]),
        c: parseFloat(d[4])
      }));
      setCandleData(formatted);
    };

    fetchCandles();
    const interval = setInterval(fetchCandles, 60000);
    return () => clearInterval(interval);
  }, []);

  // 📊 ETH Candle Chart Loader
  useEffect(() => {
    const fetchEthCandles = async () => {
      try {
        const res = await fetch('https://api.binance.com/api/v3/klines?symbol=ETHUSDT&interval=1m&limit=50');
        const data = await res.json();
        const formatted = data.map(d => ({
          x: new Date(d[0]),
          o: parseFloat(d[1]),
          h: parseFloat(d[2]),
          l: parseFloat(d[3]),
          c: parseFloat(d[4])
        }));
        setEthCandleData(formatted);
      } catch (err) {
        console.error('ETH candle fetch error:', err);
      }
    };

    fetchEthCandles();
    const interval = setInterval(fetchEthCandles, 60000);
    return () => clearInterval(interval);
  }, []);

  // 📊 XRP Candle Chart Loader
  useEffect(() => {
    const fetchXrpCandles = async () => {
      try {
        const res = await fetch('https://api.binance.com/api/v3/klines?symbol=XRPUSDT&interval=1m&limit=50');
        const data = await res.json();
        const formatted = data.map(d => ({
          x: new Date(d[0]),
          o: parseFloat(d[1]),
          h: parseFloat(d[2]),
          l: parseFloat(d[3]),
          c: parseFloat(d[4])
        }));
        setXrpCandleData(formatted);
      } catch (err) {
        console.error('XRP candle fetch error:', err);
      }
    };

    fetchXrpCandles();
    const interval = setInterval(fetchXrpCandles, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="firstpage-wrapper">
      <header className="header">
        <h1>🤖 Vortrex AI Bot</h1>
        <p>Trade Smarter on USDT Pairs – BNB, ETH, XRP</p>
      </header>

      <section className="stats">
        <div className="card">
          <h3>💰 Volume Traded</h3>
          <p>$24.3M</p>
        </div>
        <div className="card">
          <h3>📈 Daily Profit</h3>
          <p className="profit">+{profits.bnb}%</p>
        </div>
        <div className="card">
          <h3>🔁 Trades Today</h3>
          <p>349572</p>
        </div>
        <div className="card">
          <h3>👥 Active Users</h3>
          <p className="active">41596 </p>
        </div>
      </section>

      <section className="live-trades">
        <h2>📊 Live Trade Pairs</h2>
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Pair</th>
                <th>Buy @</th>
                <th>Sell @</th>
                <th>Profit %</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>BNB/USDT</td>
                <td>${(prices.bnb * 0.98).toFixed(2)}</td>
                <td>${(prices.bnb * 1.01).toFixed(2)}</td>
                <td>+{profits.bnb}%</td>
              </tr>
              <tr>
                <td>ETH/USDT</td>
                <td>${(prices.eth * 0.98).toFixed(2)}</td>
                <td>${(prices.eth * 1.01).toFixed(2)}</td>
                <td>+{profits.eth}%</td>
              </tr>
              <tr>
                <td>XRP/USDT</td>
                <td>${(prices.xrp * 0.98).toFixed(3)}</td>
                <td>${(prices.xrp * 1.01).toFixed(3)}</td>
                <td>+{profits.xrp}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* BNB Chart */}
      <section className="live-chart">
        <h2>📉 BNB/USDT 1-min Chart</h2>
        <div className="chart-container">
          <Chart
            type="candlestick"
            data={{
              datasets: [
                {
                  label: 'BNB/USDT',
                  data: candleData,
                  color: {
                    up: '#00ff84',
                    down: '#ff4d4f',
                    unchanged: '#aaa'
                  }
                }
              ]
            }}
            options={{
              responsive: true,
              scales: {
                x: { type: 'time', time: { unit: 'minute' }, ticks: { color: '#ccc' } },
                y: { beginAtZero: false, ticks: { color: '#ccc' } }
              },
              plugins: { legend: { display: false } }
            }}
          />
        </div>
      </section>

      {/* ETH Chart */}
      <section className="live-chart">
        <h2>📉 ETH/USDT 1-min Chart</h2>
        <div className="chart-container">
          <Chart
            type="candlestick"
            data={{
              datasets: [
                {
                  label: 'ETH/USDT',
                  data: ethCandleData,
                  color: {
                    up: '#00ff84',
                    down: '#ff4d4f',
                    unchanged: '#aaa'
                  }
                }
              ]
            }}
            options={{
              responsive: true,
              scales: {
                x: { type: 'time', time: { unit: 'minute' }, ticks: { color: '#ccc' } },
                y: { beginAtZero: false, ticks: { color: '#ccc' } }
              },
              plugins: { legend: { display: false } }
            }}
          />
        </div>
      </section>

      {/* XRP Chart */}
      <section className="live-chart">
        <h2>📉 XRP/USDT 1-min Chart</h2>
        <div className="chart-container">
          <Chart
            type="candlestick"
            data={{
              datasets: [
                {
                  label: 'XRP/USDT',
                  data: xrpCandleData,
                  color: {
                    up: '#00ff84',
                    down: '#ff4d4f',
                    unchanged: '#aaa'
                  }
                }
              ]
            }}
            options={{
              responsive: true,
              scales: {
                x: { type: 'time', time: { unit: 'minute' }, ticks: { color: '#ccc' } },
                y: { beginAtZero: false, ticks: { color: '#ccc' } }
              },
              plugins: { legend: { display: false } }
            }}
          />
        </div>
      </section>
    </div>
  );
};

export default FirstPage;
